import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ApiCallingService } from "../../../services/api-calling.service";
import { ConfigApi } from "../../../config-api";
import { Globals } from "../../../common/global";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { NgForm } from "@angular/forms";

@Component({
  selector: "app-instant-access-details",
  templateUrl: "./instant-access-details.component.html",
  styleUrls: ["./instant-access-details.component.css"],
})
export class InstantAccessDetailsComponent implements OnInit {
  @ViewChild("specialityform", { static: true }) specialityform: NgForm =
    Object.create(null);

  lockId = "";
  lockDetails: any = null;
  accessRecords: any[] = [];
  loading = true;
  editMode = false;
  currentRecord: any = null;

  // Form model
  accessForm = {
    email: "",
    duration: 0,
    timing: "Hour",
    _id: "",
  };

  constructor(
    private route: ActivatedRoute,
    private apiCallingService: ApiCallingService,
    private modalService: NgbModal,
    public Globals: Globals,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.lockId = params["id"];
      this.getLockDetails();
      this.getAccessRecords();
    });
  }

  getLockDetails() {
    this.apiCallingService
      .callAPI(
        {},
        "GET",
        ConfigApi.URLS.TRACK_ASSETS.GET_LOCK_DETAILS + this.lockId
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.lockDetails = res.data || {
              _id: this.lockId,
              lock_name: "Unknown Lock",
              lock_type: "Unknown Type",
            };
          }
        },
        (error) => {
          console.error("Error fetching lock details:", error);
          this.lockDetails = {
            _id: this.lockId,
            lock_name: "Unknown Lock",
            lock_type: "Unknown Type",
          };
        }
      );
  }

  getAccessRecords() {
    this.loading = true;
    this.apiCallingService
      .callAPI(
        {},
        "GET",
        ConfigApi.URLS.TRACK_ASSETS.LIST_BY_LOCKID + this.lockId
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.accessRecords = res.data || [];
            this.loading = false;
          }
        },
        (error) => {
          console.error("Error fetching instant access records:", error);
          this.loading = false;
          this.accessRecords = [];
        }
      );
  }

  openModal(content: any, record: any = null) {
    this.editMode = !!record;

    if (record) {
      this.currentRecord = record;
      this.accessForm = {
        email: record.email,
        duration: record.duration,
        timing: record.time,
        _id: record._id,
      };
    } else {
      this.currentRecord = null;
      this.accessForm = {
        email: "",
        duration: 1,
        timing: "Hour",
        _id: "",
      };
    }

    this.modalService.open(content, { centered: true });
  }

  submitForm(form: NgForm, modal: any) {
    if (form.invalid) {
      form.control.markAllAsTouched();
      return;
    }

    const isEmployee = localStorage.getItem("is_loginEmployee") === "true";
    const operatorId = isEmployee
      ? this.Globals.getUser().operator_id._id
      : this.Globals.getUser()._id;

    if (this.editMode && this.currentRecord) {
      // Update existing record
      const payload = {
        id: this.currentRecord._id,
        duration: this.accessForm.duration,
        email: this.accessForm.email,
        time: this.accessForm.timing,
      };

      this.apiCallingService
        .callAPI(payload, "PATCH", ConfigApi.URLS.TRACK_ASSETS.CREATE)
        .subscribe(
          (res) => {
            if (this.apiCallingService.handleSuccess(res)) {
              this.toastr.success(res.msg);
              this.modalService.dismissAll();
              this.getAccessRecords();
            }
          },
          (error) => {
            this.toastr.error(error.error.msg, "Error");
          }
        );
    } else {
      // Create new record
      const payload = {
        duration: this.accessForm.duration,
        email: this.accessForm.email,
        lock_id: this.lockId,
        operator_id: operatorId,
        time: this.accessForm.timing,
      };

      this.apiCallingService
        .callAPI(payload, "POST", ConfigApi.URLS.TRACK_ASSETS.CREATE)
        .subscribe(
          (res) => {
            if (this.apiCallingService.handleSuccess(res)) {
              this.toastr.success(res.msg);
              this.modalService.dismissAll();
              this.getAccessRecords();
            }
          },
          (error) => {
            this.toastr.error(error.error.msg, "Error");
          }
        );
    }
  }

  confirmDeleteAccess(accessId: string) {
    this.apiCallingService
      .callAPI({}, "DELETE", ConfigApi.URLS.TRACK_ASSETS.DELETE + accessId)
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Access deleted successfully");
            this.modalService.dismissAll();
            this.getAccessRecords();
          } else {
            this.toastr.error("Failed to delete access");
          }
        },
        (error) => {
          const errorMessage =
            error?.error?.msg || "Server error while deleting";
          this.toastr.error(errorMessage, "Error");
        }
      );
  }

  formatDate(dateString: string): string {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  getRemainingTime(record: any): string {
    if (!record.createdAt || !record.duration || !record.time)
      return record.isExpired == true ? "Expired" : "Active";

    const createdAt = new Date(record.createdAt);
    const now = new Date();

    const expiryTime = new Date(createdAt);

    switch (record.time) {
      case "Hour":
        expiryTime.setHours(expiryTime.getHours() + record.duration);
        break;
      case "Day":
        expiryTime.setDate(expiryTime.getDate() + record.duration);
        break;
      case "Week":
        expiryTime.setDate(expiryTime.getDate() + record.duration * 7);
        break;
    }

    if (now > expiryTime) return "Expired";

    const diffMs = expiryTime.getTime() - now.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs}h ${diffMins}m remaining`;
  }

  isExpired(record: any): boolean {
    return this.getRemainingTime(record) === "Expired";
  }
}
